import { Fragment, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Download, Printer } from 'lucide-react';
import { legalCatalog, legalLinks, legalPreview, legalText, safeLegalHref } from '../lib/legal';
import { NotFoundPage } from './StaticPages';

function inline(text: string): ReactNode[] {
  const parts = text.split(/(\[\[[A-Z0-9_]+\]\]|\[[^\]\n]+\]\([^\s)]+\))/g);
  return parts.map((part, index) => {
    if (/^\[\[/.test(part)) return <mark className="legal-placeholder" key={index}>{part}</mark>;
    const match = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
    if (match) {
      const href = safeLegalHref(match[2]);
      return href ? <a key={index} href={href} rel="noopener noreferrer">{match[1]}</a> : <Fragment key={index}>{part}</Fragment>;
    }
    return <Fragment key={index}>{part}</Fragment>;
  });
}

// Deliberately limited to the syntax in the approved sources. React escapes all text;
// source HTML is never interpreted. Unrecognised lines remain visible as paragraphs.
export function LegalMarkdown({ content }: { content: string }) {
  const lines = content.split('\n');
  const elements: ReactNode[] = [];
  for (let i = 0; i < lines.length;) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }
    const heading = /^(#{1,3}) (.+)$/.exec(line);
    if (heading) {
      const Tag = `h${heading[1].length}` as 'h1' | 'h2' | 'h3';
      elements.push(<Tag id={'section-' + i} key={i}>{inline(heading[2])}</Tag>); i++; continue;
    }
    if (line.startsWith('|') && /^\|[\s:|-]+\|$/.test(lines[i + 1] || '')) {
      const start = i;
      const cells = (row: string) => row.trim().slice(1, -1).split('|').map(cell => cell.trim());
      const headers = cells(line); i += 2;
      const rows: string[][] = [];
      while (lines[i]?.startsWith('|')) rows.push(cells(lines[i++]));
      elements.push(<div key={start} className="legal-table-scroll" tabIndex={0} role="region" aria-label={'Tabela: ' + headers.join(', ')}><table><thead><tr>{headers.map((cell, j) => <th scope="col" key={j}>{inline(cell)}</th>)}</tr></thead><tbody>{rows.map((row, j) => <tr key={j}>{row.map((cell, k) => <td key={k}>{inline(cell)}</td>)}</tr>)}</tbody></table></div>); continue;
    }
    if (line.startsWith('- ')) {
      const start = i; const items: string[] = [];
      while (lines[i]?.startsWith('- ')) items.push(lines[i++].slice(2));
      elements.push(<ul key={start}>{items.map((item, j) => <li key={j}>{inline(item)}</li>)}</ul>); continue;
    }
    const start = i; const paragraph = [lines[i++]];
    while (i < lines.length && lines[i].trim() && !/^(#{1,3} |\||- )/.test(lines[i])) paragraph.push(lines[i++]);
    elements.push(<p key={start}>{paragraph.map((part, j) => <Fragment key={j}>{j > 0 && (paragraph[j - 1].endsWith('  ') ? <br /> : ' ')}{inline(part.trimEnd())}</Fragment>)}</p>);
  }
  return <>{elements}</>;
}

export function LegalPage({ documentKey }: { documentKey?: string }) {
  const params = useParams();
  const key = documentKey || params.documentKey;
  const documents = params.version ? legalCatalog.archives : legalCatalog.documents;
  const document = documents.find(row => row.key === key && (!params.version || row.version === params.version));
  const available = document && (document.status === 'published' || legalPreview);
  if (!document) return <NotFoundPage />;
  if (!available) return <div className="page narrow"><h1>Dokumenty serwisu</h1><p>Dokumenty są przygotowywane do publikacji.</p><Link to="/kontakt">Kontakt</Link></div>;
  const headings = document.content.split('\n').flatMap((line, i) => line.startsWith('## ') ? [{ id: 'section-' + i, title: line.slice(3) }] : []);
  function download() {
    const url = URL.createObjectURL(new Blob([document!.content], { type: 'text/plain;charset=utf-8' }));
    const anchor = window.document.createElement('a'); anchor.href = url; anchor.download = document!.version + '.md'; anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  return <div className="page legal-page">
    {document.status !== 'published' && <aside className="legal-preview" role="note">{legalText.previewNotice}</aside>}
    <nav className="legal-navigation" aria-label="Dokumenty serwisu">{legalLinks.map(([route, label]) => <Link key={route} to={route} aria-current={document.route === route ? 'page' : undefined}>{label}</Link>)}</nav>
    <div className="legal-actions"><button type="button" className="button subtle" onClick={() => window.print()}><Printer size={18} /> Drukuj / zapisz PDF</button><button type="button" className="button subtle" onClick={download}><Download size={18} /> Pobierz treść</button><Link to="/dokumenty/archiwum">Archiwum dokumentów</Link></div>
    <details className="legal-toc"><summary>Spis treści</summary><ol>{headings.map(heading => <li key={heading.id}><a href={'#' + heading.id}>{heading.title}</a></li>)}</ol></details>
    <article className="legal-document"><LegalMarkdown content={document.content} /></article>
    <footer className="legal-fingerprint"><p>SHA-256 treści UTF-8: <code>{document.sha256}</code></p></footer>
  </div>;
}

export function LegalArchivePage() {
  return <div className="page narrow"><h1>Archiwum dokumentów</h1>{legalCatalog.archives.length ? <ul>{legalCatalog.archives.map(doc => <li key={doc.key + doc.version}><Link to={'/dokumenty/archiwum/' + doc.key + '/' + encodeURIComponent(doc.version)}>{doc.title} — {doc.version}</Link></li>)}</ul> : <p>Nie opublikowano jeszcze archiwalnych wersji dokumentów.</p>}<Link to="/zasady">Przejdź do regulaminu</Link></div>;
}
